import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  countryByCode,
  languageByCode,
  sportById,
  type EventAccess,
  type Role,
  type TickerEntry,
  type TickerState,
} from '@rabbitfuel/shared';
import { api, type EventDetailResponse } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { eventAccent, useAppearance } from '../lib/appearance.ts';
import { formatMeters } from '../components/GeofencePicker.tsx';
import { WeatherCard } from '../components/WeatherCard.tsx';
import { Footer, Topbar } from '../components/Chrome.tsx';
import { LiveTicker } from '../components/Ticker.tsx';
import { dateLong, statusLabel, statusTag } from '../lib/format.ts';
import { loadSession, saveSession } from '../lib/session.ts';

const roles: { id: Role; title: string; sub: string }[] = [
  { id: 'athlete', title: 'Athlet:in', sub: 'Ich starte hier — Startnummer nötig' },
  { id: 'spectator', title: 'Zuschauer:in', sub: 'Ich schaue zu oder filme von der Strecke' },
  { id: 'organizer', title: 'Veranstalter', sub: 'Ich mache die Regie — Konto nötig' },
];

export function EventDetail() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { brand } = useAppearance();
  const [access, setAccess] = useState<EventAccess | null>(null);
  const [data, setData] = useState<EventDetailResponse | null>(null);
  const [live, setLive] = useState<{ ticker: TickerState; feed: TickerEntry[] } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const joinRef = useRef<HTMLDivElement | null>(null);

  const [role, setRole] = useState<Role>('spectator');
  const [name, setName] = useState('');
  const [bib, setBib] = useState('');
  const [pin, setPin] = useState('');
  const [demoPin, setDemoPin] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getEvent(slug)
      .then((r) => {
        setData(r);
        setLive({ ticker: r.ticker, feed: r.tickerFeed });
      })
      .catch((e: Error) => setLoadError(e.message));
  }, [slug]);

  /**
   * Der Ticker auf dieser Seite wird abgefragt statt gepusht. Ein WebSocket
   * wuerde hier als Zuschauer im Event zaehlen -- wer nur die Beschreibung
   * liest, sitzt aber nicht im Stream. Zehn Sekunden reichen fuer einen
   * Spielstand, und die Antwort ist ein paar Kilobyte gross.
   */
  useEffect(() => {
    if (data?.event.status !== 'live') return;
    const id = window.setInterval(() => {
      api.getTicker(slug).then(setLive).catch(() => undefined);
    }, 10_000);
    return () => window.clearInterval(id);
  }, [slug, data?.event.status]);

  // Ob die Regie-Option ueberhaupt sinnvoll ist, weiss nur der Server.
  useEffect(() => {
    if (authLoading) return;
    api
      .access(slug)
      .then((r) => setAccess(r.access))
      .catch(() => setAccess({ level: null, canDirect: false, canManage: false }));
  }, [slug, user, authLoading]);

  /** Direkt in den Stream — ohne Formular, als Gast. */
  const enterStream = () => {
    const existing = loadSession(slug);
    if (!existing) {
      saveSession(slug, {
        role: 'spectator',
        displayName: name.trim() || `Gast ${Math.floor(Math.random() * 900 + 100)}`,
      });
    }
    navigate(`/events/${slug}/live`);
  };

  const join = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) return setFormError('Bitte einen Anzeigenamen eintragen.');
    setBusy(true);
    try {
      if (role === 'athlete') {
        if (!bib.trim()) throw new Error('Athlet:innen brauchen ihre Startnummer.');
        const { found, participant } = await api.lookupBib(slug, bib.trim());
        if (!found) throw new Error('Diese Startnummer steht nicht in der Startliste.');
        saveSession(slug, { role, displayName: name.trim() || participant!.name, bib: bib.trim() });
        navigate(`/events/${slug}/senden`);
        return;
      }
      if (role === 'organizer') {
        // Mit Regierecht reicht das Konto; sonst bleibt die PIN als Notfall.
        if (!access?.canDirect && !pin.trim()) {
          throw new Error('Bitte anmelden oder die Veranstalter-PIN eingeben.');
        }
        saveSession(slug, {
          role,
          displayName: name.trim(),
          organizerPin: pin.trim() || undefined,
        });
        navigate(`/events/${slug}/regie`);
        return;
      }
      saveSession(slug, { role, displayName: name.trim() });
      navigate(`/events/${slug}/live`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Beitritt fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="app">
        <Topbar />
        <main className="shell section">
          <div className="notice">Event nicht gefunden ({loadError}).</div>
          <p style={{ marginTop: 16 }}>
            <Link className="btn btn--ghost" to="/">
              Zurück zur Übersicht
            </Link>
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app">
        <Topbar />
        <main className="shell section">
          <div className="empty">Event wird geladen …</div>
        </main>
      </div>
    );
  }

  const { event } = data;
  const sport = sportById(event.sport);
  const country = countryByCode(event.country);
  const language = languageByCode(event.language);
  const paragraphs = event.description.split(/\n{2,}/).filter((p) => p.trim());

  return (
    <div className="app">
      <Topbar />
      <main>
        <section className={`detail-hero${event.coverUrl ? ' has-cover' : ''}`} style={{ ['--accent' as string]: eventAccent(event.accent, brand) }}>
          {event.coverUrl && <img className="detail-hero__cover" src={event.coverUrl} alt="" />}
          <div className="shell detail-hero__inner">
            <div className="detail-hero__top">
              {event.logoUrl && <img className="detail-hero__logo" src={event.logoUrl} alt={`Logo ${event.club}`} />}
              <div>
                <div className="row gap-12" style={{ flexWrap: 'wrap' }}>
                  <span className={statusTag[event.status]}>
                    {event.status === 'live' && <span className="dot dot--pulse" />}
                    {statusLabel[event.status]}
                  </span>
                  <span className="eyebrow eyebrow--on-dark">
                    {event.club}
                  </span>
                </div>
                <h1>{event.title}</h1>
                <div className="detail-hero__meta">
                  <span>
                    {sport.icon} {event.discipline}
                  </span>
                  <span>{dateLong.format(new Date(event.startsAt))} Uhr</span>
                  <span>
                    {country.flag} {event.location}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-hero__cta">
              <button type="button" className="btn btn--lg" onClick={enterStream}>
                {event.status === 'live' ? '● Zum Livestream' : 'Zum Stream'}
              </button>
              <button
                type="button"
                className="btn btn--lg btn--onstage"
                onClick={() => joinRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              >
                Als Athlet:in oder Regie beitreten
              </button>
              {event.status === 'live' && (
                <span className="detail-hero__hint">
                  {event.liveSources} {event.liveSources === 1 ? 'Kamera' : 'Kameras'} senden gerade · {event.viewers}{' '}
                  im Stream
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="shell detail-grid">
            <div className="stack gap-24">
              <div>
                <h2 className="detail-h2">Über das Event</h2>
                <div className="prose">
                  {paragraphs.length ? (
                    paragraphs.map((text, i) => <p key={i}>{text}</p>)
                  ) : (
                    <p className="hint">Der Veranstalter hat noch keine Beschreibung hinterlegt.</p>
                  )}
                </div>
              </div>

              {live && live.ticker.mode !== 'off' && (
                <div>
                  <h2 className="detail-h2">Live-Ticker</h2>
                  <LiveTicker
                    ticker={live.ticker}
                    feed={live.feed}
                    sport={event.sport}
                    limit={20}
                    live={event.status === 'live'}
                  />
                  {event.status !== 'live' && (
                    <p className="hint" style={{ marginTop: 8 }}>
                      Der Ticker aktualisiert sich automatisch, sobald das Event läuft.
                    </p>
                  )}
                </div>
              )}

              {/* Nach dem Ende interessiert das Wetter niemanden mehr. */}
              {event.status !== 'finished' && <WeatherCard slug={event.slug} />}

              <div>
                <h2 className="detail-h2">Details</h2>
                <dl className="factlist">
                  <div>
                    <dt>Sportart</dt>
                    <dd>
                      {sport.icon} {sport.label}
                    </dd>
                  </div>
                  <div>
                    <dt>Disziplin</dt>
                    <dd>{event.discipline}</dd>
                  </div>
                  <div>
                    <dt>Start</dt>
                    <dd>{dateLong.format(new Date(event.startsAt))} Uhr</dd>
                  </div>
                  <div>
                    <dt>Ort</dt>
                    <dd>{event.location || '—'}</dd>
                  </div>
                  <div>
                    <dt>Land</dt>
                    <dd>
                      {country.flag} {country.label}
                    </dd>
                  </div>
                  <div>
                    <dt>Stream-Sprache</dt>
                    <dd>{language.label}</dd>
                  </div>
                  <div>
                    <dt>Veranstalter</dt>
                    <dd>{event.club}</dd>
                  </div>
                  <div>
                    <dt>Startliste</dt>
                    <dd>{event.participantCount} eingetragen</dd>
                  </div>
                  {event.geofence && (
                    <div>
                      <dt>Sendebereich</dt>
                      <dd>
                        {event.geofence.mode === 'point' ? 'Umkreis' : 'Strecke'} ·{' '}
                        {formatMeters(event.geofence.radiusMeters)}
                        <span className="hint" style={{ display: 'block', fontWeight: 400 }}>
                          {event.geofence.enforcement === 'block'
                            ? 'Senden nur vor Ort möglich'
                            : 'Standort wird der Regie angezeigt'}
                        </span>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="card">
                <div className="card__head">
                  <h3>Startliste</h3>
                  <span className="tag" style={{ marginLeft: 'auto' }}>
                    {event.participants.length}
                  </span>
                </div>
                <ul className="startlist">
                  {event.participants.map((p) => (
                    <li key={p.id}>
                      <span className="bib">{p.bib}</span>
                      <span>{p.name}</span>
                      <span className="hint" style={{ marginLeft: 'auto' }}>
                        {p.category ?? p.team ?? ''}
                      </span>
                    </li>
                  ))}
                  {event.participants.length === 0 && <li className="empty">Noch keine Startnummern eingetragen.</li>}
                </ul>
              </div>
            </div>

            <div className="stack gap-16 detail-side" ref={joinRef}>
              <div className="card">
                <div className="card__head">
                  <h3>Event beitreten</h3>
                </div>
                <form className="card__body stack gap-16" onSubmit={join}>
                  <div className="stack gap-8">
                    <label style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--ink-500)' }}>Ich bin …</label>
                    <div className="role-cards role-cards--stack">
                      {roles.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          className={`role-card${role === r.id ? ' is-active' : ''}`}
                          onClick={() => setRole(r.id)}
                        >
                          <b>{r.title}</b>
                          <span>{r.sub}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="field">
                    <label htmlFor="name">Anzeigename im Chat</label>
                    <input
                      id="name"
                      className="input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="z. B. Mira S."
                      maxLength={40}
                    />
                  </div>

                  {role === 'athlete' && (
                    <div className="field">
                      <label htmlFor="bib">Startnummer</label>
                      <input
                        id="bib"
                        className="input"
                        value={bib}
                        onChange={(e) => setBib(e.target.value)}
                        placeholder="z. B. 101"
                      />
                      <span className="hint">
                        Wird gegen die Startliste geprüft — analog angemeldete Starter:innen stehen genauso drin wie
                        online angemeldete.
                      </span>
                    </div>
                  )}

                  {role === 'organizer' && (
                    <div className="stack gap-12">
                      {access?.canDirect ? (
                        <div className="notice notice--ok">
                          Angemeldet als <b>{user?.email}</b> —{' '}
                          {access.level === 'admin'
                            ? 'Admin dieses Events'
                            : access.level === 'deputy'
                              ? 'Vize dieses Events'
                              : 'auf der Regie-Liste'}
                          .
                        </div>
                      ) : user ? (
                        <div className="notice">
                          <b>{user.email}</b> steht nicht auf der Regie-Liste dieses Events. Der Admin kann dich
                          eintragen — oder du nimmst die Veranstalter-PIN.
                        </div>
                      ) : (
                        <div className="notice notice--info">
                          Für die Regie brauchst du ein Konto.{' '}
                          <Link className="linklike" to={`/anmelden?weiter=${encodeURIComponent(`/events/${slug}`)}`}>
                            Anmelden
                          </Link>{' '}
                          oder unten die Event-PIN nutzen.
                        </div>
                      )}

                      {!access?.canDirect && (
                        <div className="field">
                          <label htmlFor="pin">Veranstalter-PIN (Notfall-Zugang)</label>
                          <input
                            id="pin"
                            className="input"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            placeholder="4-stellig"
                            inputMode="numeric"
                          />
                          <span className="hint">
                            {demoPin ? (
                              <>
                                Demo-PIN für dieses Event: <b>{demoPin}</b>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="btn btn--sm btn--quiet"
                                onClick={() => api.demoPin(slug).then(setDemoPin).catch(() => undefined)}
                              >
                                Demo-PIN anzeigen
                              </button>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {formError && <div className="notice">{formError}</div>}

                  <button className="btn btn--lg btn--block" type="submit" disabled={busy}>
                    {role === 'organizer'
                      ? 'Regie öffnen'
                      : role === 'athlete'
                        ? 'Als Athlet:in senden'
                        : 'Als Zuschauer:in beitreten'}
                  </button>
                </form>
              </div>

              <div className="card">
                <div className="card__body stack gap-8">
                  <h3 style={{ fontSize: 14 }}>Nur zuschauen?</h3>
                  <p className="hint">Ohne Formular rein — du landest als Gast im Hauptstream und kannst mitchatten.</p>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={enterStream}>
                    Direkt zum Hauptstream
                  </button>
                </div>
              </div>

              {access?.canDirect && (
                <p className="hint" style={{ textAlign: 'center' }}>
                  <Link to={`/events/${slug}/einstellungen`} className="linklike">
                    Event bearbeiten
                  </Link>
                  {access.canManage ? ' · Bilder, Zone und Regie-Liste' : ' · Bilder und Angaben'}
                </p>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
